import { useState } from "react";
import { format, isValid, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Form fields store dates as ISO `yyyy-MM-dd` strings (what the API expects and
// what the native <input type="date"> produced), so this picker takes and emits
// that same string — a drop-in replacement for the native inputs.
const ISO_FORMAT = "yyyy-MM-dd";

export interface DatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  /** Dates before this are disabled (e.g. today, to block past start dates). */
  minDate?: Date;
  /** Dates after this are disabled (e.g. today, for a date of birth). */
  maxDate?: Date;
  disabled?: boolean;
  "aria-invalid"?: boolean;
  className?: string;
  /**
   * Caption style. "dropdown" shows month + year dropdowns (useful for
   * far-back dates like a date of birth); the year range spans minDate..maxDate
   * (falling back to ~120 years and today).
   */
  captionLayout?: "label" | "dropdown";
}

function parseIso(value?: string): Date | undefined {
  if (!value) return undefined;
  const parsed = parse(value, ISO_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
}

export function DatePicker({
  value,
  onChange,
  id,
  placeholder = "Select date",
  minDate,
  maxDate,
  disabled,
  "aria-invalid": ariaInvalid,
  className,
  captionLayout = "label",
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseIso(value);

  const disabledMatchers = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  // The month/year dropdowns need an explicit range to be useful; bound it to
  // maxDate (or today) and back to minDate (or ~120 years — enough for any DOB).
  const dropdown = captionLayout === "dropdown";
  const endMonth = dropdown ? (maxDate ?? new Date()) : undefined;
  const startMonth =
    dropdown && endMonth
      ? (minDate ?? new Date(endMonth.getFullYear() - 120, 0))
      : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            id={id}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            className={cn(
              "w-full justify-between font-normal",
              !selected && "text-text-muted",
              className,
            )}
          />
        }
      >
        {selected ? format(selected, "MMM d, yyyy") : placeholder}
        <CalendarIcon data-icon="inline-end" />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? maxDate ?? minDate ?? new Date()}
          captionLayout={captionLayout}
          startMonth={startMonth}
          endMonth={endMonth}
          disabled={disabledMatchers.length ? disabledMatchers : undefined}
          onSelect={(date) => {
            if (date) onChange(format(date, ISO_FORMAT));
            setOpen(false);
          }}
          // Focus a day when the popover opens so keyboard users land inside the
          // calendar; this is the expected date-picker interaction.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
